import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate, statusClass } from "../lib/utils";
import {
  ArrowLeft, Buildings, User, Wallet, Package, Warning, ArrowsLeftRight,
  CurrencyEur, Pause, Play, NotePencil, Check, X as XIcon, Calendar,
  Plus, Receipt, Lightning, Envelope, Phone, MapPin, IdentificationBadge,
  Storefront, DownloadSimple, ClockCounterClockwise, PencilSimple,
  CheckCircle, LockKey, PaperPlaneTilt, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import BottomSheet from "../components/BottomSheet";
import { FileThumb } from "../components/FileThumb";

const EMAIL_TEMPLATES = [
  { key: "welcome_setup_guide", label: "Benvenuto & Setup Guide", icon: Envelope },
  { key: "activation_welcome", label: "Account Attivato", icon: CheckCircle },
  { key: "password_reset", label: "Reset Password", icon: LockKey },
  { key: "wallet_balance", label: "Notifica Wallet Basso/Zero", icon: CurrencyEur },
  { key: "order_blocked", label: "Ordine Bloccato", icon: Warning },
];
const TEMPLATE_LABELS = Object.fromEntries(EMAIL_TEMPLATES.map((t) => [t.key, t.label]));
const PURPOSE_LABELS = {
  admin_resend_welcome_setup_guide: "Benvenuto & Setup Guide",
  admin_resend_activation_welcome: "Account Attivato",
  password_reset: "Reset Password",
  admin_resend_wallet_balance: "Notifica Wallet Basso/Zero",
  admin_resend_order_blocked: "Ordine Bloccato",
  activation_welcome: "Account Attivato (automatica)",
  wallet_recharge_success: "Ricarica Wallet (automatica)",
  order_blocked: "Ordine Bloccato (automatica)",
  low_balance: "Wallet Basso (automatica)",
  empty_balance: "Wallet Vuoto (automatica)",
  negative_balance: "Wallet Negativo (automatica)",
  blocked_orders_reminder: "Solleciti Ordini Bloccati (automatica)",
  signup_notification: "Notifica interna registrazione",
};
function prettyPurpose(p) {
  return PURPOSE_LABELS[p] || (p || "").replace(/_/g, " ");
}

export default function AdminClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [feedFilter, setFeedFilter] = useState("all"); // all|orders|recharges
  const [showAdjust, setShowAdjust] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emailLog, setEmailLog] = useState([]);

  const load = async () => {
    try {
      const [d, a, el] = await Promise.all([
        api.get(`/admin/clients/${clientId}/detail`),
        api.get(`/admin/clients/${clientId}/audit`).catch(() => ({ data: [] })),
        api.get(`/admin/clients/${clientId}/email-log`).catch(() => ({ data: [] })),
      ]);
      setData(d.data);
      setAuditLog(a.data || []);
      setEmailLog(el.data || []);
    } catch (e) {
      toast.error("Cliente non trovato");
      navigate("/admin/clients");
    }
  };
  useEffect(() => { load(); }, [clientId]);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/clients/${clientId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers?.["content-disposition"] || "";
      const m = disp.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `export_${clientId.slice(0,8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Esportato ✓");
    } catch (e) {
      toast.error("Errore export");
    } finally { setExporting(false); }
  };

  if (!data) return <div className="p-8 text-[var(--tf-muted)]">Caricamento…</div>;

  const { client, wallet, user, counts, feed } = data;
  const isPrivate = (client.entity_type || "business") === "private";
  const isSuspended = client.status === "suspended";
  const displayName = isPrivate
    ? `${client.first_name || ""} ${client.last_name || ""}`.trim() || client.company_name
    : client.company_name;

  const toggleSuspend = async () => {
    const next = isSuspended ? "active" : "suspended";
    try {
      await api.patch(`/admin/clients/${clientId}`, { status: next });
      toast.success(isSuspended ? "Riattivato" : "Sospeso");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const filteredFeed = feed.filter((i) => {
    if (feedFilter === "all") return true;
    if (feedFilter === "orders") return i.kind === "order";
    if (feedFilter === "recharges") return i.direction === "credit";
    if (feedFilter === "branding") return i.kind === "branding_order";
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-client-detail">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate("/admin/clients")}
          className="w-10 h-10 rounded-2xl border border-[var(--tf-border)] flex items-center justify-center text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition"
          data-testid="back-clients"
          aria-label="Indietro"
        >
          <ArrowLeft size={16} weight="bold" />
        </button>
        <PageHeader
          eyebrow="Cliente"
          title={displayName}
          subtitle={user?.email || client.contact_email || ""}
          chip={isPrivate ? "tf-chip-lavender" : "tf-chip-sky"}
          icon={isPrivate ? User : Buildings}
        />
      </div>

      {/* Wallet + counters top row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="tf-card p-4 md:p-5 col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold flex items-center gap-1.5">
            <Wallet size={12} weight="fill" /> Saldo wallet
          </div>
          <div
            className="font-display font-black text-3xl md:text-4xl mt-2 tf-numeric text-[var(--tf-ink)]"
            style={{ textShadow: "none" }}
            data-testid="detail-balance"
          >
            {fmtEur(wallet.balance)}
          </div>
          <div className="text-[11px] text-[var(--tf-muted)] mt-1">
            Piano <b className="text-[var(--tf-ink)]">{client.subscription_plan || "—"}</b>
            {client.subscription_period && ` · ${client.subscription_period}`}
          </div>
        </div>
        <MiniStat icon={Package} label="Ordini" value={counts.orders} />
        <MiniStat icon={Warning} label="Bloccati" value={counts.blocked} highlight={counts.blocked > 0} />
      </div>

      {/* Quick Toolbar */}
      <div className="tf-card p-3 md:p-4 grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3" data-testid="admin-toolbar">
        <ToolBtn onClick={() => setShowAdjust(true)} icon={CurrencyEur} label="Wallet" testid="tool-adjust" />
        <ToolBtn onClick={toggleSuspend} icon={isSuspended ? Play : Pause} label={isSuspended ? "Riattiva" : "Sospendi"} testid="tool-suspend" danger={!isSuspended} />
        <ToolBtn onClick={() => setShowNotes(true)} icon={NotePencil} label="Note" testid="tool-notes" />
        <ToolBtn onClick={doExport} icon={DownloadSimple} label={exporting ? "…" : "CSV"} testid="tool-export" />
        <ToolBtn onClick={() => setShowAudit(true)} icon={ClockCounterClockwise} label="Storico" testid="tool-audit" />
        <Link
          to={`/admin/pricelists?client=${clientId}`}
          className="rounded-2xl border border-[var(--tf-border)] hover:bg-[var(--tf-bg)] transition flex flex-col items-center gap-1.5 text-xs font-bold text-[var(--tf-ink)]"
          data-testid="tool-pricelist"
          style={{ padding: "14px 12px" }}
        >
          <Receipt size={18} weight="fill" />
          Listino
        </Link>
      </div>

      {/* Client fiscal details */}
      <div className="tf-card p-4 md:p-5" data-testid="fiscal-card">
        <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold mb-3 flex items-center gap-1.5">
          <IdentificationBadge size={12} weight="fill" /> {isPrivate ? "Anagrafica privato" : "Dati aziendali"}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          {isPrivate ? (
            <>
              <ReadField icon={User} label="Nome" value={client.first_name} />
              <ReadField icon={User} label="Cognome" value={client.last_name} />
              <ReadField icon={IdentificationBadge} label="C.F." value={client.codice_fiscale} />
            </>
          ) : (
            <>
              <ReadField icon={Buildings} label="Ragione sociale" value={client.company_name} />
              <ReadField icon={IdentificationBadge} label="P.IVA" value={client.vat_number} />
              <ReadField icon={IdentificationBadge} label="C.F." value={client.codice_fiscale} />
              <ReadField icon={Envelope} label="SDI/PEC" value={client.sdi_or_pec} />
            </>
          )}
          <ReadField icon={Envelope} label="Email" value={client.contact_email || user?.email} />
          <ReadField icon={Phone} label="Telefono" value={client.business_phone} />
          <ReadField
            icon={MapPin}
            label="Indirizzo"
            value={
              [client.address_line1, client.city, client.postal_code, client.province, client.country]
                .filter(Boolean)
                .join(", ")
            }
            fullWidth
          />
          <ShopIdField client={client} clientId={clientId} auditLog={auditLog} lang={lang} onSaved={load} />
        </div>
      </div>

      {/* Communications Center */}
      <CommsSection clientId={clientId} emailLog={emailLog} lang={lang} onSent={load} />

      {/* Activity Feed */}
      <div className="tf-card overflow-hidden" data-testid="client-activity-feed">
        <div className="flex items-center justify-between p-4 md:p-5 pb-3 gap-3 flex-wrap">
          <div>
            <div className="font-display font-bold text-lg">Attività recente</div>
            <div className="text-xs text-[var(--tf-muted)] mt-0.5">Ordini e ricariche del cliente</div>
          </div>
          <div className="inline-flex p-1 rounded-full bg-[var(--tf-bg)] border border-[var(--tf-border)]">
            {[
              { k: "all", label: "Tutti" },
              { k: "orders", label: "Ordini" },
              { k: "recharges", label: "Ricariche" },
              { k: "branding", label: "Branding" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFeedFilter(f.k)}
                className={`px-3 py-1.5 text-xs font-bold rounded-full transition-all ${
                  feedFilter === f.k
                    ? "bg-[var(--tf-ink)] text-white"
                    : "text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
                }`}
                data-testid={`feed-filter-${f.k}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-[var(--tf-border)] divide-y divide-[var(--tf-border)]">
          {filteredFeed.length === 0 && (
            <div className="p-8 text-center text-[var(--tf-muted)] text-sm">Nessuna attività ancora</div>
          )}
          {filteredFeed.map((item) => {
            const isCredit = item.direction === "credit";
            const isAuto = item.kind === "auto_recharge";
            const isBranding = item.kind === "branding_order";
            const isBlocked = item.kind === "order" && item.status === "blocked";
            const Icon = isAuto ? Lightning : isCredit ? Plus : isBranding ? Sparkle : Package;
            const iconBg = isAuto ? "#ECFCC9" : isCredit ? "#D1F0E0" : isBranding ? "#FCE7D6" : isBlocked ? "#FBDDE1" : "#F5F2E8";
            const iconColor = isAuto ? "#3E5A0A" : isCredit ? "#1F6E4A" : isBranding ? "#9A5A1E" : isBlocked ? "#A11534" : "#193529";
            return (
              <div
                key={`${item.kind}-${item.id}`}
                className="flex items-center gap-3 md:gap-4 p-4 md:px-5"
                data-testid={`feed-item-${item.id}`}
              >
                <div
                  className="w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: iconBg, color: iconColor }}
                >
                  <Icon size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">
                    {isCredit ? (isAuto ? "Auto-ricarica" : "Ricarica") : item.label}
                    {isBlocked && (
                      <span className="ml-2 tf-status-pill tf-status-error text-[9px] align-middle">Bloccato</span>
                    )}
                    {isBranding && item.status && (
                      <span className="ml-2 tf-status-pill tf-status-neutral text-[9px] align-middle">{item.status}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--tf-muted)] mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <Calendar size={11} weight="bold" />
                    <span>{fmtDate(item.date, lang)}</span>
                    {item.subtitle && (
                      <>
                        <span className="opacity-40">·</span>
                        <span className="truncate">{item.subtitle}</span>
                      </>
                    )}
                  </div>
                  {isBranding && item.files?.length > 0 && (
                    <div className="flex items-center gap-2 mt-2" data-testid={`branding-files-${item.id}`}>
                      {item.files.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noreferrer" data-testid={`branding-file-link-${item.id}-${idx}`}>
                          <FileThumb url={url} size={32} testid={`branding-file-thumb-${item.id}-${idx}`} />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  className={`font-display font-black text-sm md:text-base tf-numeric flex-shrink-0 text-right ${
                    isCredit ? "text-[var(--tf-success)]" : "text-[#C7476A]"
                  }`}
                >
                  {isCredit ? "+" : "−"}{fmtEur(item.amount)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showAdjust && (
        <AdjustSheet
          client={client}
          onClose={() => setShowAdjust(false)}
          onDone={() => { setShowAdjust(false); load(); }}
        />
      )}
      {showNotes && (
        <NotesSheet
          client={client}
          onClose={() => setShowNotes(false)}
          onDone={() => { setShowNotes(false); load(); }}
        />
      )}
      {showAudit && (
        <BottomSheet
          open
          onClose={() => setShowAudit(false)}
          subtitle="Cronologia modifiche admin"
          title={client.company_name}
          testId="audit-sheet"
        >
          <div className="divide-y divide-[var(--tf-border)]">
            {auditLog.length === 0 && (
              <div className="p-6 text-center text-sm text-[var(--tf-muted)]">
                <ClockCounterClockwise size={28} weight="duotone" className="mx-auto mb-2 opacity-60" />
                Nessuna modifica registrata ancora
              </div>
            )}
            {auditLog.map((log) => (
              <div key={log.id} className="py-3 flex items-start gap-3" data-testid={`audit-${log.id}`}>
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: log.action === "wallet_adjust" ? "#ECFCC9" : log.action === "export_csv" ? "#DAECFA" : "#F5F2E8",
                    color: log.action === "wallet_adjust" ? "#3E5A0A" : log.action === "export_csv" ? "#1F5A93" : "#193529",
                  }}
                >
                  {log.action === "wallet_adjust" ? <CurrencyEur size={15} weight="fill" />
                    : log.action === "export_csv" ? <DownloadSimple size={15} weight="fill" />
                    : <PencilSimple size={15} weight="fill" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm text-[var(--tf-ink)]">
                    {log.action === "wallet_adjust" ? "Aggiustamento wallet"
                      : log.action === "export_csv" ? "Export CSV"
                      : log.action === "update" ? "Aggiornamento profilo"
                      : log.action === "shop_id_assigned" ? "Store Myze collegato"
                      : log.action === "reconcile_orders" ? "Ordini riconciliati"
                      : log.action === "email_sent" ? "Email inviata"
                      : log.action}
                  </div>
                  <div className="text-[11px] text-[var(--tf-muted)] mt-0.5">
                    {log.actor_email} · {fmtDate(log.created_at, lang)}
                  </div>
                  {log.notes && <div className="text-xs text-[var(--tf-ink)] mt-1 italic">&ldquo;{log.notes}&rdquo;</div>}
                  {log.changes && Object.keys(log.changes).length > 0 && (
                    <div className="text-[11px] mt-1 space-y-0.5">
                      {Object.entries(log.changes).map(([k, v]) => (
                        <div key={k} className="flex gap-1 items-baseline text-[var(--tf-muted)]">
                          <span className="uppercase tracking-widest font-bold text-[10px]">{k}</span>
                          {v && typeof v === "object" && "from" in v ? (
                            <span>: <span className="line-through">{String(v.from ?? "—")}</span> → <b className="text-[var(--tf-ink)]">{String(v.to ?? "—")}</b></span>
                          ) : (
                            <span>: <b className="text-[var(--tf-ink)]">{typeof v === "number" ? v.toFixed(2) : String(v)}</b></span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </BottomSheet>
      )}
    </div>
  );
}

function CommsSection({ clientId, emailLog, lang, onSent }) {
  const [sending, setSending] = useState(null);
  const send = async (key) => {
    setSending(key);
    try {
      const res = await api.post(`/admin/clients/${clientId}/send-email`, { template: key });
      toast.success(`Email "${TEMPLATE_LABELS[key]}" inviata a ${res.data.to}`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio email");
    } finally {
      setSending(null);
    }
  };
  const last = emailLog[0];
  return (
    <div className="tf-card p-4 md:p-5" data-testid="comms-section">
      <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold mb-3 flex items-center gap-1.5">
        <PaperPlaneTilt size={12} weight="fill" /> Comunicazioni
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {EMAIL_TEMPLATES.map((t) => (
          <button
            key={t.key}
            onClick={() => send(t.key)}
            disabled={sending === t.key}
            className="rounded-2xl border border-[var(--tf-border)] hover:bg-[var(--tf-bg)] hover:border-[var(--tf-ink)] transition flex items-center gap-2 text-xs font-bold text-[var(--tf-ink)] px-3 py-3 disabled:opacity-50"
            data-testid={`send-email-${t.key}`}
          >
            <t.icon size={16} weight="fill" className="flex-shrink-0" />
            <span className="truncate">{sending === t.key ? "Invio…" : t.label}</span>
          </button>
        ))}
      </div>
      <div className="text-[11px] text-[var(--tf-muted)] mt-3 pt-3 border-t border-[var(--tf-border)]" data-testid="last-email-sent">
        {last ? (
          <>Ultima email inviata: <b className="text-[var(--tf-ink)]">{prettyPurpose(last.purpose)}</b> il {fmtDate(last.created_at, lang)}</>
        ) : (
          "Nessuna email inviata ancora a questo cliente."
        )}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`tf-card p-4 md:p-5 ${highlight ? "border-2 border-[var(--tf-error)]" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold flex items-center gap-1.5">
        <Icon size={12} weight="fill" /> {label}
      </div>
      <div
        className={`font-display font-black text-2xl md:text-3xl mt-2 tf-numeric ${highlight ? "text-[var(--tf-error)]" : "text-[var(--tf-ink)]"}`}
        style={{ textShadow: "none" }}
      >
        {value}
      </div>
    </div>
  );
}

function ToolBtn({ onClick, icon: Icon, label, danger, testid }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-2xl border border-[var(--tf-border)] hover:bg-[var(--tf-bg)] transition flex flex-col items-center gap-1.5 text-xs font-bold ${danger ? "hover:!border-[var(--tf-error)] hover:text-[var(--tf-error)]" : "text-[var(--tf-ink)]"}`}
      data-testid={testid}
      style={{ padding: "14px 12px" }}
    >
      <Icon size={18} weight="fill" />
      {label}
    </button>
  );
}

function ReadField({ icon: Icon, label, value, fullWidth }) {
  return (
    <div className={fullWidth ? "col-span-2 md:col-span-3" : ""}>
      <div className="text-[9px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold flex items-center gap-1 mb-1">
        <Icon size={10} weight="bold" /> {label}
      </div>
      <div className="font-display font-bold text-[13px] text-[var(--tf-ink)] break-words">
        {value || <span className="text-[var(--tf-muted)] font-normal">—</span>}
      </div>
    </div>
  );
}

function ShopIdField({ client, clientId, auditLog, lang, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(client.myze_shop_id || "");
  const [saving, setSaving] = useState(false);
  const hasShop = !!client.myze_shop_id;
  const assignedLog = (auditLog || []).find((a) => a.action === "shop_id_assigned");

  const save = async () => {
    const v = value.trim();
    if (!v) { toast.error("Inserisci uno Shop ID"); return; }
    setSaving(true);
    try {
      const res = await api.patch(`/admin/clients/${clientId}`, { myze_shop_id: v });
      const rec = res.data?.reconciled;
      if (rec && rec.count > 0) {
        toast.success(`Store collegato — ${rec.charged} ordin${rec.charged === 1 ? "e" : "i"} sbloccat${rec.charged === 1 ? "o" : "i"} su ${rec.count} in attesa`);
      } else {
        toast.success("Store Myze collegato");
      }
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <div className="col-span-2 md:col-span-3" data-testid="shop-id-field">
      <div className="text-[9px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold flex items-center gap-1 mb-1">
        <Storefront size={10} weight="bold" /> Shop ID Myze
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Es. 83708608858"
            className="flex-1 px-3 py-2 rounded-xl border border-[var(--tf-border)] bg-white text-sm font-mono font-bold text-[var(--tf-ink)] focus:outline-none focus:border-[var(--tf-ink)]"
            data-testid="shop-id-input"
          />
          <button onClick={save} disabled={saving} className="tf-btn tf-btn-primary !px-3 !py-2" data-testid="shop-id-save">
            <Check size={14} weight="bold" /> {saving ? "…" : "Salva"}
          </button>
          <button onClick={() => { setEditing(false); setValue(client.myze_shop_id || ""); }} className="tf-btn tf-btn-ghost !px-3 !py-2" data-testid="shop-id-cancel">
            <XIcon size={14} weight="bold" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display font-bold text-[13px] text-[var(--tf-ink)]">
            {hasShop ? client.myze_shop_id : <span className="text-[var(--tf-muted)] font-normal">Non collegato</span>}
          </div>
          {!hasShop && (
            <span className="tf-status-pill tf-status-error text-[9px]" data-testid="shop-id-missing-badge">
              Store Myze non ancora collegato
            </span>
          )}
          <button
            onClick={() => setEditing(true)}
            className="text-[11px] font-bold underline text-[var(--tf-ink)] hover:opacity-70"
            data-testid="shop-id-edit-btn"
          >
            {hasShop ? "Modifica" : "Collega ora"}
          </button>
        </div>
      )}
      {assignedLog && (
        <div className="text-[11px] text-[var(--tf-muted)] mt-1" data-testid="shop-id-assigned-history">
          Associato da <b className="text-[var(--tf-ink)]">{assignedLog.actor_email}</b> il {fmtDate(assignedLog.created_at, lang)}
        </div>
      )}
    </div>
  );
}

function AdjustSheet({ client, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState("credit");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Importo non valido"); return; }
    setSaving(true);
    try {
      const signed = direction === "credit" ? amt : -amt;
      await api.post(`/admin/wallet/${client.id}/adjust`, { amount: signed, note });
      toast.success("Wallet aggiornato");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };
  return (
    <BottomSheet open onClose={onClose} subtitle="Aggiusta wallet" title={client.company_name} testId="adjust-sheet">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setDirection("credit")}
            className={`rounded-2xl border-2 py-4 flex items-center justify-center gap-2 font-bold text-sm transition ${direction === "credit" ? "border-[var(--tf-success)] bg-[#D1F0E0] text-[var(--tf-success)]" : "border-[var(--tf-border)] text-[var(--tf-muted)]"}`}
            data-testid="adj-credit"
          >
            <Plus size={16} weight="bold" /> Accredito
          </button>
          <button
            onClick={() => setDirection("debit")}
            className={`rounded-2xl border-2 py-4 flex items-center justify-center gap-2 font-bold text-sm transition ${direction === "debit" ? "border-[var(--tf-error)] bg-[#FBDDE1] text-[var(--tf-error)]" : "border-[var(--tf-border)] text-[var(--tf-muted)]"}`}
            data-testid="adj-debit"
          >
            <XIcon size={16} weight="bold" /> Addebito
          </button>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold mb-1.5 block">Importo</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tf-muted)] font-display font-bold text-lg pointer-events-none">€</span>
            <input
              type="number"
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full pl-10 pr-4 py-4 rounded-2xl border border-[var(--tf-border)] bg-white font-display font-black text-2xl text-[var(--tf-ink)] tf-numeric focus:outline-none focus:border-[var(--tf-ink)] focus:ring-2 focus:ring-[var(--tf-primary)]/30"
              placeholder="0,00"
              data-testid="adj-amount"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold mb-1.5 block">Nota (opzionale)</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-4 py-3.5 rounded-2xl border border-[var(--tf-border)] bg-white text-sm font-display font-bold text-[var(--tf-ink)] focus:outline-none focus:border-[var(--tf-ink)]"
            placeholder="Es. Bonus fedeltà"
            data-testid="adj-note"
          />
        </div>
        <button
          onClick={submit}
          disabled={saving}
          className="tf-btn tf-btn-primary w-full"
          data-testid="adj-submit"
          style={{ padding: "14px 20px" }}
        >
          <Check size={16} weight="bold" /> {saving ? "…" : "Conferma"}
        </button>
      </div>
    </BottomSheet>
  );
}

function NotesSheet({ client, onClose, onDone }) {
  const [notes, setNotes] = useState(client.notes || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/clients/${client.id}`, { notes });
      toast.success("Note salvate");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  return (
    <BottomSheet open onClose={onClose} subtitle="Note interne" title={client.company_name} testId="notes-sheet">
      <div className="space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">Note visibili solo agli admin</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={8}
          className="w-full px-4 py-3.5 rounded-2xl border border-[var(--tf-border)] bg-white text-sm font-display text-[var(--tf-ink)] focus:outline-none focus:border-[var(--tf-ink)] resize-none"
          placeholder="Aggiungi note interne su questo cliente…"
          data-testid="notes-textarea"
        />
        <button
          onClick={submit}
          disabled={saving}
          className="tf-btn tf-btn-primary w-full"
          data-testid="notes-submit"
          style={{ padding: "14px 20px" }}
        >
          <Check size={16} weight="bold" /> {saving ? "…" : "Salva note"}
        </button>
      </div>
    </BottomSheet>
  );
}
